import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import PropTypes from "prop-types";

import { Scrollbar } from "../scrollbar/index.jsx";
import { InfiniteScrollContainer, INFINITE_SCROLL_AXIS } from "./infinite.jsx";
import { Spinner, SPINNER_SIZES } from "../../feedback/spinner/index.jsx";

import styles from "./infinitescroll.css";

export { RefreshAtEnd } from "./refresh.at.end.jsx";
export { InfiniteContainer, InfiniteElement } from "./container.jsx";
export { InfiniteScrollList } from "./infinitescrolllist.jsx";
export { InfiniteScrollContainer, INFINITE_SCROLL_AXIS };

export const SCROLLBAR_THICKNESS = {
  THIN: "thin",
  MEDIUM: "medium",
  THICK: "thick"
};

export const SCROLLBAR_DIRECTION = {
  UP: "up",
  DOWN: "down",
  BOTH: "both"
};

function InfiniteScrollComponent(props) {
  const {
    children,
    isLoading,
    loaderRenderer = () => (
      <Spinner className={styles.spinner} size={SPINNER_SIZES.SMALL} />
    ),
    className,
    style,
    onEndReached,
    scrollRef,
    onScroll,
    endOffset = 0,
    thickness = SCROLLBAR_THICKNESS.MEDIUM,
    direction = SCROLLBAR_DIRECTION.DOWN,
    canTriggerOnEnd
  } = props;

  const [lastScrollheight, setLastScrollheight] = useState(0);
  const [isDirectionUp, setIsDirectionUp] = useState(false);
  const currentScrollRef = useRef(null);

  const checkScroll = useCallback(function checkScrollCallback() {
    if (typeof canTriggerOnEnd === "boolean" && canTriggerOnEnd) return false;
    else if (typeof canTriggerOnEnd === "object" && canTriggerOnEnd[direction])
      return false;
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = useCallback(
    function scrollConditionCheckingEvent(e) {
      if (isLoading) {
        return;
      }
      const sRef = currentScrollRef && currentScrollRef.current;
      if (sRef) {
        setTimeout(() => {
          const y = sRef.scroll().ratio.y;
          if (direction === SCROLLBAR_DIRECTION.BOTH) {
            if (checkScroll(SCROLLBAR_DIRECTION.UP) && y === 0)
              onEndReached && onEndReached(SCROLLBAR_DIRECTION.UP);
            if (checkScroll(SCROLLBAR_DIRECTION.DOWN) && y === 1)
              onEndReached && onEndReached(SCROLLBAR_DIRECTION.DOWN);
            setIsDirectionUp(y === 0);
            onScroll && onScroll(e);
          } else if (direction === SCROLLBAR_DIRECTION.UP && y === 0) {
            if (checkScroll(SCROLLBAR_DIRECTION.UP))
              onEndReached && onEndReached(SCROLLBAR_DIRECTION.UP);
            setIsDirectionUp(true);
          } else if (direction === SCROLLBAR_DIRECTION.DOWN && y === 1) {
            if (checkScroll(SCROLLBAR_DIRECTION.DOWN))
              onEndReached && onEndReached(SCROLLBAR_DIRECTION.DOWN);
          } else {
            onScroll && onScroll(e);
          }
        });
      }
    },
    [isLoading, direction, checkScroll, onEndReached, onScroll]
  );

  useEffect(
    function scrollDirectionUpdate() {
      if (currentScrollRef.current) {
        if (scrollRef) {
          scrollRef.current = currentScrollRef.current;
        }
        if (direction === SCROLLBAR_DIRECTION.UP) {
          currentScrollRef.current.scrollToBottom();
        }
      }
    },
    [currentScrollRef, scrollRef, direction]
  );

  useEffect(
    function scrollHeightUpdate() {
      let timerId = null;
      if (!checkScroll(SCROLLBAR_DIRECTION.UP)) return;
      if (!currentScrollRef.current) return;
      if (isDirectionUp) {
        if (!isLoading) {
          timerId = setTimeout(() => {
            currentScrollRef.current.scroll({
              y:
                currentScrollRef.current.scroll().max.y -
                lastScrollheight -
                endOffset
            });
          }, 100);
        } else {
          setLastScrollheight(currentScrollRef.current.scroll().max.y);
        }
      } else {
        currentScrollRef.current.scroll({ y: `+=${1}` });
      }
      return () => {
        if (timerId) {
          clearTimeout(timerId);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, isDirectionUp]
  );

  return (
    <Scrollbar
      className={className}
      ref={currentScrollRef}
      onScroll={handleScroll}
      style={style}
      thickness={thickness}
    >
      {isDirectionUp && isLoading && loaderRenderer && loaderRenderer()}
      {children}
      {!isDirectionUp && isLoading && loaderRenderer && loaderRenderer()}
    </Scrollbar>
  );
}

/**
 * This component is used to achieve infinite scrolling of the container
 */
export const InfiniteScroll = forwardRef((props, ref) => {
  return <InfiniteScrollComponent scrollRef={ref} {...props} />;
});

InfiniteScroll.propTypes = {
  /** A children node that is been render the component */
  children: PropTypes.node,
  /** An boolean state which will be used to show the loaderRenderer  */
  isLoading: PropTypes.bool,
  /** Custom node which will be used as a fallback if the loader state is true */
  loaderRenderer: PropTypes.func,
  /** Custom callback will be fired if the user reached the end/top */
  onEndReached: PropTypes.func,
  /** Event will be fired when user scrolls inside the container */
  onScroll: PropTypes.func,
  /** A class string will be passed */
  className: PropTypes.string,
  /** A ref of the element */
  scrollRef: PropTypes.any,
  /** Inline style can be passed for any overridden */
  style: PropTypes.object,
  /** Offset value which helps the scroll to its original pos + offset value only applied if the direction is up */
  endOffset: PropTypes.number,
  /** Thickness of the scroll thumb */
  thickness: PropTypes.string,
  /** It's refers to the direction in which scroll happens */
  direction: PropTypes.string,
  /** It's either a boolean when the direction is up/down or an object if the direction is both, actually to tell whether to call the onEndReached after a scrollend */
  canTriggerOnEnd: PropTypes.oneOfType([PropTypes.bool, PropTypes.object])
};

InfiniteScroll.defaultProps = {
  loaderRenderer: () => (
    <Spinner className={styles.spinner} size={SPINNER_SIZES.SMALL} />
  ),
  endOffset: 0,
  thickness: SCROLLBAR_THICKNESS.MEDIUM,
  direction: SCROLLBAR_DIRECTION.DOWN
};

InfiniteScrollComponent.propTypes = InfiniteScroll.propTypes;
